import React, {
  CSSProperties,
  ElementType,
  HTMLAttributes,
  forwardRef,
} from 'react';
import styles from './styles.module.css';

type ResponsiveProp<T> =
  | T
  | {
      base?: T;
      sm?: T;
      md?: T;
      lg?: T;
      xl?: T;
    };

export interface GridContainerProps extends HTMLAttributes<HTMLDivElement> {
  as?: ElementType;
  areas?: ResponsiveProp<string>;
  rows?: ResponsiveProp<string>;
  columns?: ResponsiveProp<string>;
  autoRows?: ResponsiveProp<string>;
  autoColumns?: ResponsiveProp<string>;
  autoFlow?: CSSProperties['gridAutoFlow'];
  gap?: CSSProperties['gap'];
  gapX?: CSSProperties['columnGap'];
  gapY?: CSSProperties['rowGap'];
  alignItems?: CSSProperties['alignItems'];
  justifyContent?: CSSProperties['justifyContent'];
}

export const GridContainer = forwardRef<HTMLDivElement, GridContainerProps>(
  (
    {
      children,
      as: Tag = 'div',
      areas,
      rows,
      columns,
      autoRows,
      autoColumns,
      autoFlow,
      gap = 0,
      gapX,
      gapY,
      alignItems,
      justifyContent,
      className,
      style,
      ...rest
    },
    ref
  ) => {
    const classes = [styles.gridTemplate, className].filter(Boolean).join(' ');

    const inlineStyle: CSSProperties = {
      display: 'grid',
      columnGap: gapX ?? gap,
      rowGap: gapY ?? gap,
      gridAutoFlow: autoFlow,
      ...getResponsiveVariables(areas, 'gta'), // grid-template-areas
      ...getResponsiveVariables(rows, 'gtr'), // grid-template-rows
      ...getResponsiveVariables(columns, 'gtc'), // grid-template-columns,
      ...getResponsiveVariables(autoRows, 'gar'), // grid-auto-rows
      ...getResponsiveVariables(autoColumns, 'gac'), // grid-auto-columns
    };
    if (alignItems) inlineStyle.alignItems = alignItems;
    if (justifyContent) inlineStyle.justifyContent = justifyContent;

    return (
      <Tag
        ref={ref}
        className={classes}
        style={{ ...inlineStyle, ...style }}
        {...rest}
      >
        {children}
      </Tag>
    );
  }
);

const getResponsiveVariables = (
  propValue: ResponsiveProp<string> | undefined,
  variablePrefix: string
): CSSProperties => {
  if (!propValue) return {};

  const styles: { [key: string]: string } = {};

  if (typeof propValue === 'string') {
    styles[`--${variablePrefix}-base`] = propValue;
  } else {
    for (const key of Object.keys(propValue)) {
      styles[`--${variablePrefix}-${key}`] = (propValue as any)[key];
    }
  }
  return styles as CSSProperties;
};
